/**
 * Core Authentication Logic
 *
 * Platform-agnostic authentication utilities.
 * Part of Batch 5a migration.
 */

import { storage } from '../adapters'

/**
 * Check if an error is a token-related error that requires re-authentication
 * @param {Error|object} error - The error to check
 * @returns {boolean} True if this is a token error
 */
export const isTokenError = (error) => {
  if (!error) return false

  const message = error.message || error.msg || ''
  const status = error.status || error.statusCode

  // Check for specific token-related error messages
  const tokenErrorPatterns = [
    'Invalid Refresh Token',
    'Refresh Token Not Found',
    'JWT',
    'token expired',
    'invalid token',
    'Token is expired',
    'Token has been revoked'
  ]

  const isMessageMatch = tokenErrorPatterns.some(pattern =>
    message.toLowerCase().includes(pattern.toLowerCase())
  )

  // Status 400 with auth context is often a token error
  const isStatusMatch = status === 400 || status === 401

  return isMessageMatch || (isStatusMatch && message.toLowerCase().includes('token'))
}

/**
 * Calculate if a user is fully authenticated
 * With deferred email verification, users are authenticated once they have a session.
 * Email verification is tracked separately via companies.email_verified.
 * @param {object|null} user - The user object
 * @param {object|null} session - The session object
 * @returns {boolean} True if user is authenticated
 */
export const isUserAuthenticated = (user, session) => {
  return !!user && !!session
}

/**
 * Check if user needs email verification (legacy — always returns false)
 * Email verification is now deferred and tracked via companies.email_verified.
 * Kept for backward compatibility.
 * @param {object|null} user - The user object
 * @returns {boolean} Always returns false
 */
export const needsEmailVerification = (user) => {
  return false
}

/**
 * Check if user needs company creation after email verification
 * @param {object|null} user - The user object
 * @returns {boolean} True if user has pending company creation
 */
export const needsCompanyCreation = (user) => {
  return !!user?.user_metadata?.pending_company_creation
}

/**
 * Clear auth-related storage keys
 * Uses the storage adapter for cross-platform compatibility
 * @returns {Promise<void>}
 */
export const clearAuthStorage = async () => {
  try {
    // Get all keys from storage
    const keys = await storage.getAllKeysAsync()

    // Filter and remove auth-related keys
    const authKeys = keys.filter(key =>
      key.includes('supabase') || key.includes('auth')
    )

    for (const key of authKeys) {
      await storage.removeAsync(key)
    }
  } catch (error) {
    console.error('[authCore] Error clearing auth storage:', error)
    // Don't throw - clearing storage is best effort
  }
}

/**
 * Create a timeout promise for auth operations
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} operation - Name of the operation for error message
 * @returns {Promise<never>} A promise that rejects after timeout
 */
export const createAuthTimeout = (timeoutMs, operation = 'Auth operation') => {
  return new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`${operation} timeout`)), timeoutMs)
  )
}

/**
 * Execute an async operation with timeout protection
 * @param {Promise} promise - The promise to execute
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} operation - Name of the operation for error message
 * @returns {Promise<any>} The result of the promise or throws on timeout
 */
export const withTimeout = async (promise, timeoutMs, operation = 'Operation') => {
  return Promise.race([
    promise,
    createAuthTimeout(timeoutMs, operation)
  ])
}

/**
 * Build the password reset redirect URL
 * @param {string} baseUrl - The base URL of the application
 * @returns {string} The full redirect URL
 */
export const getPasswordResetRedirectUrl = (baseUrl) => {
  return `${baseUrl}/reset-password`
}

/**
 * Did sign-in fail only because the account's email is not confirmed yet?
 * Email signups start unconfirmed (#428), so this is the "check your inbox"
 * state, not a bad password.
 * @param {Error|object} error - Supabase auth error, or { code, message }
 * @returns {boolean}
 */
export const isEmailNotConfirmedError = (error) =>
  error?.code === 'email_not_confirmed' || /email not confirmed/i.test(error?.message || '')

/**
 * Parse auth error to user-friendly message
 * @param {Error|object} error - The error to parse
 * @returns {string} User-friendly error message
 */
export const parseAuthError = (error) => {
  if (!error) return 'An unknown error occurred'

  const message = error.message || error.msg || 'An error occurred'

  // Map common Supabase auth errors to user-friendly messages
  const errorMappings = {
    'Invalid login credentials': 'Invalid email or password',
    'Email not confirmed': 'Please check your email for a verification link',
    'User already registered': 'An account with this email already exists',
    'Password should be at least': 'Password is too short',
    'Unable to validate email address': 'Please enter a valid email address',
    'Invalid Refresh Token': 'Your session has expired. Please sign in again.',
    'Token is expired': 'Your session has expired. Please sign in again.'
  }

  for (const [pattern, friendlyMessage] of Object.entries(errorMappings)) {
    if (message.includes(pattern)) {
      return friendlyMessage
    }
  }

  return message
}

/**
 * Get user display name from user object
 * @param {object|null} user - The user object
 * @returns {string} Display name or email
 */
export const getUserDisplayName = (user) => {
  if (!user) return ''

  const metadata = user.user_metadata || {}

  // Try various name fields
  if (metadata.full_name) return metadata.full_name
  if (metadata.name) return metadata.name
  if (metadata.first_name && metadata.last_name) {
    return `${metadata.first_name} ${metadata.last_name}`
  }
  if (metadata.first_name) return metadata.first_name

  // Fall back to email
  return user.email || ''
}

/**
 * Extract user initials from user object
 * @param {object|null} user - The user object
 * @returns {string} User initials (1-2 characters)
 */
export const getUserInitials = (user) => {
  const displayName = getUserDisplayName(user)

  if (!displayName) return '?'

  const parts = displayName.split(' ').filter(p => p.length > 0)

  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }

  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase()
  }

  return '?'
}
